import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel"

import { CardImage } from "@/components/brand-kit/card-image"
import { alternateCardImage } from "@/lib/card-image"
import Link from "next/link"
import { RecommendedProduct, compareByIllustratedThenPriced } from "@/lib/recommendations"
import { formatProductTitle } from "@/lib/breadcrumb-utils"
import { BadgeList } from "@/components/badges/badge-list"
import { badgesFromRecommended } from "@/components/badges/from-product"
import { productCardCtaName } from "@/lib/card-link-names"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"

// Extracted to avoid re-creating objects on every render

interface RecentViewsBlockProps {
  products: RecommendedProduct[]
}

export function RecentViewsBlock({ products }: RecentViewsBlockProps) {
  if (!products || products.length === 0) {
    return null
  }

  // Illustrated first, then priced — the one rail rule, from
  // lib/recommendations.ts. This block used to carry its own copy of the
  // price-only comparator and re-sorted what the selector had already
  // ordered, which put un-illustrated cards back at the front (FRIDGE-398).
  const sortedProducts = [...products].sort(compareByIllustratedThenPriced)

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="pt-4 pb-1 px-4 sm:px-5">
        {/* FRIDGE-1501: this block never held the visitor's history. Its rows come
            from getRecentlyViewed() (lib/recommendations.ts), which picks active
            products from the catalog, so it no longer says "Your Recent Views",
            "Products you viewed on your visits", "seen before" or, on each card,
            "Recently Viewed". The real
            visitor-history rail is components/brand-kit/recently-viewed-rail. */}
        <h3 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2">
          👀 More to Compare on Fridge.com
          {products.length > 1 && (
            <span className="text-sm text-gray-500 ml-2">({products.length} products)</span>
          )}
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          {products.length === 1
            ? 'A model from across our catalog'
            : 'Models from across our catalog'}
        </p>
      </div>
      <div className="px-2 sm:px-4 pb-4">
        {/* Scrollable carousel for unlimited products */}
        <Carousel
          opts={{
            align: "start",
            loop: false,
            slidesToScroll: 1,
            dragFree: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 sm:-ml-3">
            {sortedProducts.map((item) => {
              // FRIDGE-429 — ONE boolean gates the price row AND picks the CTA label.
              const showsPrice = hasRenderedPrice(item.price)
              return (
              <CarouselItem key={item.id} className="pl-2 sm:pl-3 basis-[280px] sm:basis-1/2 lg:basis-1/4">
                <div data-product-image-card className="border rounded-lg p-2.5 bg-white h-full flex flex-col hover:shadow-md transition-shadow">
                  <div className="relative mb-2">
                    {/* Canonical badge set, same array + priority order as everywhere
                        else, so Price Drop etc. persist here too. No seen_before overlay:
                        these rows are not the visitor's history (FRIDGE-1501). */}
                    <BadgeList
                      badges={badgesFromRecommended(item)}
                      variant="card"
                    />
                    <div className="aspect-square bg-white rounded-lg overflow-hidden">
                    <CardImage src={item.images?.[0] || '/placeholder.svg'} alternate={alternateCardImage(item.images?.[0] || '', item.images || [])} href={item.canonical_url || `/products/${item.id}`} alt={`${item.title} | Fridge.com`} width={200} height={200} />
                  </div>
                  </div>
                  <div className="text-xs text-[#0469f0] font-medium mb-1">Also Consider</div>
                  <Link href={item.canonical_url || `/products/${item.id}`} className="text-xs font-medium mb-2 line-clamp-2 min-h-[2rem] hover:underline">
                    {formatProductTitle(item.title)}
                  </Link>
                  <div className="mb-2">
                    {showsPrice && typeof item.price === 'number' ? (
                      <div className="flex items-baseline gap-1.5">
                        <span className={`font-bold text-base ${typeof item.originalPrice === 'number' && item.originalPrice > item.price ? 'text-green-600' : ''}`}>{`$${item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                        {typeof item.originalPrice === 'number' && item.originalPrice > item.price && (
                          <span className="text-xs text-red-500 line-through">{`$${item.originalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                        )}
                      </div>
                    ) : (
                      <div className="h-6" />
                    )}
                  </div>
                  {/* FRIDGE-1508 K1508-15: one link styled as the button it used to wrap;
                      flex + centring stand in for a button's own layout. The button's
                      mt-auto is not carried over: inside the link it never applied. */}
                  <Link href={item.canonical_url || `/products/${item.id}`} aria-label={productCardCtaName(dealCtaLabel(showsPrice), item, item.canonical_url || `/products/${item.id}`)} className="flex items-center justify-center w-full h-9 text-xs font-semibold whitespace-nowrap min-h-[36px] bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] rounded-full transition-colors">
                    {dealCtaLabel(showsPrice)}
                  </Link>
                </div>
              </CarouselItem>
              )
            })}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  )
}

